import React, { useState, useEffect, useCallback, useId } from 'react';
import { adminService, UploadPolicyAdmin } from '../../services/adminService';
import { setUploadPolicy } from '../../services/uploadPolicyService';
import { RefreshCw, AlertCircle } from 'lucide-react';
import { ConfirmDialog } from '../ConfirmDialog';
import { useToast } from '../../contexts/ToastContext';

/** 보관 기간 표기 — 0은 '무기한' */
const retentionText = (days: number) => (days > 0 ? `${days}일` : '무기한');

/**
 * 첨부 용량·개수·보관 기간 정책 관리.
 * 저장하면 서버 재시작 없이 다음 업로드부터 적용되고, 접속 중인 모든 세션에 소켓으로 전파된다.
 *
 * ⚠ 보관 기간은 이미 저장된 첨부에 소급되지 않는다 — 만료 시각이 attachments 행에 절대 시각으로
 *   박히기 때문이다. 이 사실을 화면에 적어두지 않으면 "기간을 줄였는데 안 지워진다"는 오해가 나온다.
 */
export const UploadPolicyManager: React.FC = () => {
  const { showToast } = useToast();
  const fid = useId();
  const [policy, setPolicy] = useState<UploadPolicyAdmin | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sizeInput, setSizeInput] = useState('');
  const [filesInput, setFilesInput] = useState('');
  const [retentionInput, setRetentionInput] = useState('');
  const [reason, setReason] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /** window.confirm은 Tauri WebView2에서 항상 false라 ConfirmDialog로 대체 */
  const [confirmOpen, setConfirmOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await adminService.getUploadPolicy();
      setPolicy(data);
      setSizeInput(String(data.maxFileSizeMB));
      setFilesInput(String(data.maxFiles));
      setRetentionInput(String(data.retentionDays));
    } catch (err) {
      console.error('Failed to load upload policy:', err);
      setLoadError(err instanceof Error ? err.message : '첨부 용량 설정을 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const nextSize = Number(sizeInput);
  const nextFiles = Number(filesInput);
  const nextRetention = Number(retentionInput);
  const isDirty = !!policy && (
    nextSize !== policy.maxFileSizeMB
    || nextFiles !== policy.maxFiles
    || nextRetention !== policy.retentionDays
  );
  // 제한을 좁히는 변경은 기존 사용자의 첨부를 막게 되므로 확인창을 파괴적 액션으로 표시한다.
  // 보관 기간은 '줄이는 것'과 '무기한(0) → 유한'이 둘 다 파일이 사라지는 방향이다.
  const isTightening = !!policy && (
    nextSize < policy.maxFileSizeMB
    || nextFiles < policy.maxFiles
    || (nextRetention > 0 && (policy.retentionDays === 0 || nextRetention < policy.retentionDays))
  );

  const validate = (): string | null => {
    if (!policy) return null;
    const { limits } = policy;
    if (!Number.isInteger(nextSize) || nextSize < limits.minFileSizeMB || nextSize > limits.maxFileSizeMB) {
      return `최대 용량은 ${limits.minFileSizeMB}~${limits.maxFileSizeMB} 사이의 정수(MB)여야 합니다.`;
    }
    if (!Number.isInteger(nextFiles) || nextFiles < limits.minFiles || nextFiles > limits.maxFiles) {
      return `한 번에 보낼 개수는 ${limits.minFiles}~${limits.maxFiles} 사이의 정수여야 합니다.`;
    }
    if (!Number.isInteger(nextRetention) || nextRetention < limits.minRetentionDays || nextRetention > limits.maxRetentionDays) {
      return `보관 기간은 ${limits.minRetentionDays}~${limits.maxRetentionDays} 사이의 정수(일)여야 합니다. 0은 만료 없음입니다.`;
    }
    if (reason.length > 200) return '변경 사유는 200자 이하여야 합니다.';
    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      setFormError(error);
      return;
    }
    setFormError(null);
    setConfirmOpen(true);
  };

  const performSave = async () => {
    setConfirmOpen(false);
    if (!policy) return;
    setSubmitting(true);
    try {
      const updated = await adminService.updateUploadPolicy({
        maxFileSizeMB: nextSize,
        maxFiles: nextFiles,
        retentionDays: nextRetention,
        reason: reason.trim() || undefined,
      });
      setPolicy(updated);
      setReason('');
      // 이 관리자 세션도 소켓 전파를 받지만 응답이 먼저 도착할 수 있다. 멱등이므로 즉시 반영해 둔다.
      setUploadPolicy({
        maxFileSizeMB: updated.maxFileSizeMB,
        maxFiles: updated.maxFiles,
        retentionDays: updated.retentionDays,
      });
      showToast({ type: 'success', message: '첨부 설정을 변경했습니다.' });
    } catch (err) {
      showToast({
        type: 'error',
        message: err instanceof Error ? err.message : '첨부 설정을 변경하지 못했습니다.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const limits = policy?.limits;

  return (
    <div>
      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-2">
        <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
        <div className="text-xs text-amber-800">
          <p className="font-medium">첨부 정책</p>
          <p className="mt-0.5">
            변경 사항은 서버 재시작 없이 즉시 반영되며, 접속 중인 모든 사용자에게 곧바로 적용되고 감사 로그에
            기록됩니다. 용량을 크게 늘리면 사용자 PC의 메모리 사용량과 업로드 시간도 함께 늘어납니다.
          </p>
          <p className="mt-1.5 font-medium">
            보관 기간을 바꿔도 이미 보낸 첨부의 만료일은 달라지지 않습니다. 새 값은 이후 보내는 첨부부터
            적용됩니다. 보관 기간이 지난 첨부는 원본 파일이 삭제되어 되살릴 수 없습니다.
          </p>
          <p className="mt-1.5">
            보관 기간은 직원 이용 고지에 적힌 내용입니다. 바꾸면 고지 내용이 바뀐 것으로 보고 다음 접속 때 모든
            직원에게 이용 고지 동의를 다시 받습니다.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-end mb-4">
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          새로고침
        </button>
      </div>

      {loadError && <div className="text-center py-12 text-brand-600 text-sm">{loadError}</div>}

      {!loadError && !policy && loading && (
        <div className="text-center py-12 text-slate-400 text-sm">불러오는 중…</div>
      )}

      {!loadError && policy && (
        <>
          <form onSubmit={handleSubmit} className="mb-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label htmlFor={`${fid}-size`} className="block text-xs font-medium text-slate-600 mb-1">
                  파일 하나당 최대 용량 (MB)
                </label>
                <input
                  id={`${fid}-size`}
                  type="number"
                  inputMode="numeric"
                  value={sizeInput}
                  onChange={(e) => setSizeInput(e.target.value)}
                  min={limits?.minFileSizeMB}
                  max={limits?.maxFileSizeMB}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-slate-400">
                  {limits?.minFileSizeMB}~{limits?.maxFileSizeMB}MB 사이에서 정할 수 있습니다
                </p>
              </div>
              <div>
                <label htmlFor={`${fid}-files`} className="block text-xs font-medium text-slate-600 mb-1">
                  한 번에 보낼 최대 개수
                </label>
                <input
                  id={`${fid}-files`}
                  type="number"
                  inputMode="numeric"
                  value={filesInput}
                  onChange={(e) => setFilesInput(e.target.value)}
                  min={limits?.minFiles}
                  max={limits?.maxFiles}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-slate-400">
                  {limits?.minFiles}~{limits?.maxFiles}개 사이에서 정할 수 있습니다
                </p>
              </div>
              <div>
                <label htmlFor={`${fid}-retention`} className="block text-xs font-medium text-slate-600 mb-1">
                  첨부 보관 기간 (일)
                </label>
                <input
                  id={`${fid}-retention`}
                  type="number"
                  inputMode="numeric"
                  value={retentionInput}
                  onChange={(e) => setRetentionInput(e.target.value)}
                  min={limits?.minRetentionDays}
                  max={limits?.maxRetentionDays}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-slate-400">
                  0을 넣으면 만료 없이 보관합니다
                </p>
              </div>
            </div>

            <div className="mt-3">
              <label htmlFor={`${fid}-reason`} className="block text-xs font-medium text-slate-600 mb-1">
                변경 사유 (선택)
              </label>
              <input
                id={`${fid}-reason`}
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={200}
                placeholder="예: 설계 도면 공유 요청 반영"
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-slate-400">감사 로그에 함께 기록됩니다</p>
            </div>

            {formError && <p className="mt-3 text-xs text-brand-600">{formError}</p>}

            <div className="mt-4 flex justify-end">
              <button
                type="submit"
                disabled={submitting || !isDirty}
                className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
              >
                {submitting ? '변경 중…' : '변경'}
              </button>
            </div>
          </form>

          <div className="p-3 bg-white border border-slate-200 rounded-xl text-sm text-slate-600">
            <p>
              현재 적용값 —{' '}
              <span className="font-medium text-slate-800">
                파일당 {policy.maxFileSizeMB}MB · 한 번에 {policy.maxFiles}개 · 보관 {retentionText(policy.retentionDays)}
              </span>
            </p>
            {policy.updatedAt && (
              <p className="mt-1 text-xs text-slate-400">
                마지막 변경 {new Date(policy.updatedAt).toLocaleString('ko-KR')} · {policy.updatedByName}
              </p>
            )}
          </div>
        </>
      )}

      <ConfirmDialog
        isOpen={confirmOpen}
        title="첨부 정책 변경"
        message={`첨부 정책을 파일당 ${nextSize}MB · 한 번에 ${nextFiles}개 · 보관 ${retentionText(nextRetention)}으로 바꿀까요? 접속 중인 모든 사용자에게 즉시 적용되며, 보관 기간은 이후 보내는 첨부부터 반영됩니다.`}
        confirmLabel="변경"
        danger={isTightening}
        onConfirm={performSave}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
};
